<template>
    <div class="position-relative container px-0 mt-1">
        <highcharts ref="candlestickChart"
                    class="highcharts-candlestick-chart"
                    :constructor-type="'stockChart'"
                    :options="chartOptions" />
        <div id="candlestick-export">
            <ul>
                <li id="export-png"
                    @click="exportImage('candlestick', 'image/png')">
                    <translate translation-key="charts.downloadPngImage" />
                </li>
                <li id="export-jpeg"
                    @click="exportImage('candlestick', 'image/jpeg')">
                    <translate translation-key="charts.downloadJpegImage" />
                </li>
            </ul>
        </div>
    </div>
</template>

<script>

    import { Chart } from 'highcharts-vue';
    import Highcharts from 'highcharts';
    import axios from 'axios';
    import { tippy } from 'vue-tippy';
    import { priceFormat, currencyFormat, addChartWatermark } from '../helpers/helpers';
    import highchart from '../mixins/highchart';
    import { getTrans } from '../helpers/getTrans';
    import { CDN_CHARTS_LOADER } from '../constants/assets';

    export default {
        name: 'CandlestickChart',
        components: { highcharts: Chart },
        mixins: [highchart],
        inject: ['currency_code'],
        props: {
            dataRange: {
                type: Object,
                default: () => {},
            },
        },
        data() {
            return {
                hcInstance: null,
                range: null,
                chartOptions: {
                    accessibility: {
                        enabled: false,
                    },
                    chart: {
                        height: 485,
                        zoomType: 'x',
                        animation: false,
                        marginRight: 0,
                        events: {
                            load() {
                                const initialWidth = this.chartWidth - 120;
                                const initialHeight = this.chartHeight * 0.65 - 34;
                                const fullscreenHeight = window.innerHeight - 180;
                                const fullscreenWidth = window.innerWidth - 130;

                                this.renderer.image('https://assets.bitdegree.org/crypto-prices/assets/bitdegree-watermark.png', initialWidth, initialHeight, 120, 34).add().addClass('not-fullscreen');
                                this.renderer.image('https://assets.bitdegree.org/crypto-prices/assets/bitdegree-watermark.png', fullscreenWidth, fullscreenHeight, 120, 34).add().addClass('fullscreen');
                            },
                        },
                    },
                    credits: {
                        enabled: false,
                    },
                    time: {
                        useUTC: true,
                    },
                    rangeSelector: {
                        enabled: false,
                    },
                    animation: true,
                    xAxis: {
                        crosshair: {
                            label: {
                                type: 'datetime',
                                enabled: true,
                                format: '{value:%m/%d/%Y %I:%M %p}',
                                backgroundColor: '#666666',
                            },
                        },
                        type: 'datetime',
                        dateTimeLabelFormats: {
                            day: '%d. %b',
                            hour: '%I:%M %p',
                        },
                    },
                    yAxis: [
                        {
                            plotLines: [{
                                color: 'grey',
                                dashStyle: 'dot',
                                width: 1,
                                label: {
                                    x: -75,
                                    y: 0,
                                    useHTML: true,
                                    formatter() {
                                        const { value } = this.options;

                                        return priceFormat(value, true);
                                    },
                                },
                                lineWidth: 1,
                            }],
                            height: '80%',
                            opposite: false,
                            labels: {
                                formatter(value) {
                                    return priceFormat(value.value, true, true);
                                },
                            },
                            crosshair: {
                                dashStyle: 'dot',
                                width: 1,
                                color: 'grey',
                                snap: false,
                                label: {
                                    enabled: true,
                                    useHTML: true,
                                    formatter(value) {
                                        return priceFormat(value, true);
                                    },
                                },
                            },
                            lineColor: '#e6e6e6',
                            lineWidth: 1,
                        },
                        {
                            opposite: false,
                            top: '80%',
                            height: '20%',
                            offset: 0,
                            crosshair: {
                                dashStyle: 'dot',
                                width: 1,
                                color: 'grey',
                                snap: false,
                                label: {
                                    enabled: true,
                                    formatter(value) {
                                        return priceFormat(value, true);
                                    },
                                },
                            },
                            lineColor: '#e6e6e6',
                            lineWidth: 1,
                            labels: {
                                enabled: false,
                            },
                        },
                    ],
                    navigation: {
                        menuItemHoverStyle: {
                            background: '#f8fafd',
                            color: '#000000',
                        },
                        contextMenuStyle: {
                            border: 'none',
                        },
                        buttonOptions: {
                            height: 25,
                            y: 0,
                        },
                    },
                    navigator: {
                        series: {
                            color: '#3861FB',
                            fillColor: 'rgba(56,97,251, 0.1)',
                        },
                        outlineWidth: 0,
                        maskFill: 'rgba(56, 98, 251, 0.08)',
                        xAxis: {
                            type: 'datetime',
                            dateTimeLabelFormats: {
                                day: '%d. %b',
                                hour: '%I:%M %p',
                            },
                            tickPixelInterval: 100,
                        },
                    },
                    responsive: {
                        rules: [{
                            chartOptions: {
                                chart: {
                                    marginRight: 0,
                                },
                                tooltip: {
                                    followPointer: false,
                                    positioner(labelWidth, labelHeight, point) {
                                        const tooltipX = this.chart.plotLeft;
                                        let tooltipY = (point.plotY) - this.chart.plotTop;

                                        if ((point.plotY - labelHeight - this.chart.plotTop) > this.chart.plotTop) {
                                            tooltipY = (point.plotY - 100) + this.chart.plotTop - 20;
                                        }

                                        return {
                                            x: tooltipX,
                                            y: tooltipY,
                                        };
                                    },
                                },
                            },
                            condition: {
                                maxWidth: 540,
                            },
                        }],
                    },
                    plotOptions: {
                        candlestick: {
                            color: '#ff0060',
                            lineColor: '#ff0060',
                            upColor: '#17c783',
                            upLineColor: '#17c783',
                        },
                    },
                    tooltip: {
                        followTouchMove: true,
                        followPointer: true,
                        borderColor: 'none',
                        backgroundColor: 'white',
                        borderRadius: '8',
                        shadow: false,
                        shared: true,
                        split: false,
                        useHTML: true,
                        positioner(labelWidth, labelHeight, point) {
                            let tooltipX = (point.plotX) + this.chart.plotLeft + 20;
                            let tooltipY = (point.plotY - 60) - this.chart.plotTop + labelHeight;

                            if (point.plotX + labelWidth + 20 > this.chart.plotWidth) {
                                tooltipX = (point.plotX) + this.chart.plotLeft - labelWidth - 20;
                            }

                            if ((point.plotY - labelHeight - this.chart.plotTop) > this.chart.plotTop) {
                                tooltipY = (point.plotY - 100) + this.chart.plotTop - 20;
                            }

                            return {
                                x: tooltipX,
                                y: tooltipY,
                            };
                        },
                        formatter() {
                            let { open } = this.points[0] !== undefined ? this.points[0].point : 0;
                            let { high } = this.points[0] !== undefined ? this.points[0].point : 0;
                            let { low } = this.points[0] !== undefined ? this.points[0].point : 0;
                            let { close } = this.points[0] !== undefined ? this.points[0].point : 0;

                            open = priceFormat(open);
                            high = priceFormat(high);
                            low = priceFormat(low);
                            close = priceFormat(close);

                            const volume = currencyFormat(this.points[1] !== undefined ? this.points[1].y : 0, true);

                            const date = Highcharts.dateFormat('%m/%d/%Y', this.points[0].x);

                            return '<div class="floating-tooltip p-2">'
                                + '<div class="d-flex align-items-center mb-2">'
                                + `<span class="primary">${date}</span>`
                                + '</div>'
                                + '<div class="d-flex align-items-center">'
                                + '<span style="color: #814cff" class="indicator">&#9632</span>'
                                + `<span class="sub">${this.points[0].series.userOptions.translations[0]}: &nbsp</span>`
                                + `<span class="number">${open}</span>`
                                + '</div>'
                                + '<div class="d-flex align-items-center">'
                                + '<span style="color: #814cff" class="indicator line text-center">&#124</span>'
                                + `<span class="sub">${this.points[0].series.userOptions.translations[1]}: &nbsp</span>`
                                + `<span class="number">${high}</span>`
                                + '</div>'
                                + '<div class="d-flex align-items-center">'
                                + '<span style="color: #814cff" class="indicator line text-center">&#124</span>'
                                + `<span class="sub">${this.points[0].series.userOptions.translations[2]}: &nbsp</span>`
                                + `<span class="number">${low}</span>`
                                + '</div>'
                                + '<div class="d-flex align-items-center">'
                                + '<span style="color: #814cff" class="indicator">&#9632</span>'
                                + `<span class="sub">${this.points[0].series.userOptions.translations[3]}: &nbsp</span>`
                                + `<span class="number">${close}</span>`
                                + '</div>'
                                + '<div class="d-flex align-items-center">'
                                + '<span style="color: gray" class="indicator">\u25CF</span>'
                                + `<span class="sub">${this.points[0].series.userOptions.translations[4]}: &nbsp</span>`
                                + `<span class="number">${volume}</span>`
                                + '</div>'
                                + '</div>';
                        },
                    },
                    series: [
                        {
                            type: 'candlestick',
                            data: [],
                            yAxis: 0,
                            threshold: null,
                            dataGrouping: {
                                enabled: false,
                            },
                            tooltip: {
                                valueDecimals: 2,
                            },
                            fillColor: {
                                linearGradient: {
                                    x1: 0, y1: 0, x2: 0, y2: 1,
                                },
                                stops: [
                                    [0, 'rgba(152,132,252, 0.4)'],
                                    [1, 'rgba(152,132,252, 0)'],
                                ],
                            },
                            states: {
                                inactive: {
                                    opacity: 1,
                                },
                                hover: {
                                    halo: null,
                                },
                            },
                        },
                        {
                            type: 'column',
                            data: [],
                            yAxis: 1,
                            color: '#c7c7c7',
                            states: {
                                hover: {
                                    enabled: false,
                                },
                                inactive: {
                                    opacity: 1,
                                    enabled: false,
                                },
                            },
                            dataGrouping: {
                                enabled: false,
                            },
                        },
                    ],
                    exporting: {
                        enabled: false,
                    },
                },
            };
        },
        watch: {
            dataRange() {
                this.changeRange(this.dataRange.range, this.dataRange.slug);
            },
        },
        mounted() {
            this.createTippy('candlestick-export');
            this.hcInstance = this.$refs.candlestickChart;
            if (this.dataRange.range) {
                this.changeRange(this.dataRange.range, this.dataRange.slug);
            }
            this.addChartWatermark(this.hcInstance.chart);
        },
        methods: {
            changeRange(selectedRange, slug) {
                this.hcInstance.chart.showLoading(`<img class="highchart-loader" src="${CDN_CHARTS_LOADER}"/>`);
                axios.get(`/api/cryptocurrencies/ohlc-chart/${slug}?period=${selectedRange}`)
                    .then((res) => {
                        this.chartOptions.series[0].data = res.data.ohlc;
                        this.chartOptions.series[0].translations = [
                            getTrans('cryptocurrencies.open'),
                            getTrans('cryptocurrencies.high'),
                            getTrans('cryptocurrencies.low'),
                            getTrans('cryptocurrencies.close'),
                            getTrans('cryptocurrencies.vol24h'),
                        ];
                        this.chartOptions.series[1].data = res.data.volumes;
                    })
                    .finally(() => {
                        this.range = selectedRange;
                        this.hcInstance.chart.hideLoading();
                    });
            },
            createTippy(id) {
                const element = document.getElementById(id);
                if (element) {
                    element.style.display = 'block';

                    tippy(`#export-menu-tippy-${id}`, {
                        theme: 'tip-dropdown',
                        content: element,
                        placement: 'bottom',
                        animation: 'fade',
                        interactive: true,
                        trigger: 'click',
                        aria: null,
                    });
                }
            },
            addChartWatermark,
        },
    };
</script>
